/**
 * Traducción de errores de la API de Aliados a la vista (ADR 0021).
 *
 * El backend responde con un contrato uniforme; `apiClient` ya lo normaliza a `ApiError`.
 * Acá se decide qué mostrar y a qué campo del formulario apuntar el error:
 * - Siempre se muestra el `message` del backend (fuente de verdad del texto).
 * - Si el error identifica el campo (`details.field` / `details.fieldName`), se marca ese.
 * - Si no, se cae al campo conocido por el `code` estable (ADR 0021: sin parsear mensajes).
 */
import { ApiError } from '@/lib/http/apiClient';

export interface AllyApiError {
  /** Mensaje del backend, listo para mostrar. */
  message: string;
  /** Nombre del campo del formulario a marcar, si el error lo identifica. */
  field?: string;
}

/**
 * Códigos estables → campo del formulario (unicidad de aliado: documento y email —Q5—).
 * `ALIADO_ADMIN_DUPLICATED` (F11: email de admin ya en uso) apunta a la lista de admins.
 * `ALIADO_ADMIN_NOT_PENDING` / `ALIADO_ADMIN_NOT_FOUND` no marcan campo: se muestran como
 * mensaje (409/404 del reenroll).
 */
const CODE_FIELD: Record<string, string> = {
  ALIADO_DOCUMENT_DUPLICATED: 'documentNumber',
  ALIADO_EMAIL_DUPLICATED: 'email',
  ALIADO_ADMIN_DUPLICATED: 'admins',
};

/**
 * Redacción de los duplicados de unicidad, espejo del backend (TG-118). La reusa el
 * chequeo consultivo por paso, que no recibe el `message` del POST (solo el booleano/
 * la lista de tomados) pero debe decir lo mismo para no inventar otro texto.
 */
export const ALLY_DUPLICATE_MESSAGES = {
  documentNumber: 'Ya existe un Aliado con ese documento',
  email: 'Ya existe un Aliado con ese email',
  adminEmail: (email: string) => `El email ${email} ya pertenece a un usuario`,
};

/** Extrae el campo de `details` (convención ADR 0021) si viene. */
function fieldFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const record = details as Record<string, unknown>;
  for (const key of ['field', 'fieldName'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * Describe un error de aliado para mostrarlo en la página. `fallbackMessage` ajusta el
 * texto genérico según la acción (alta, agregar admins, regenerar invitación).
 */
export function describeAllyApiError(
  error: unknown,
  fallbackMessage = 'No se pudo crear el aliado.',
): AllyApiError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      field: fieldFromDetails(error.details) ?? CODE_FIELD[error.code],
    };
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: fallbackMessage };
}
